// Shared, testable resume-file handler for the AI-interview pre-start screen.
//
// Why this exists as its own module: the upload UI previously had TWO upload
// mechanisms stacked on top of each other — an antd <Upload.Dragger beforeUpload>
// AND a nested <label htmlFor> + hidden <input onChange> that called
// `e.preventDefault()` then a manual `input.click()`. That redundant pair made
// the click-to-open-file-dialog path unreliable (the label's default activation
// was cancelled and the programmatic click was swallowed inside the Dragger), so
// for many candidates the file dialog never opened and the request to
// /ai-interview/parse-resume was never sent. The interview backend was healthy
// the whole time — the file simply never left the browser.
//
// The fix collapses everything to ONE path: antd's Dragger opens the dialog and
// also handles drag-and-drop, and both feed this single handler. Keeping the
// logic here (instead of inline in the 1k-line instruction component) lets us
// unit-test it directly with `node --test`, the same way ttsAudio / pcmWorklet
// are tested.

export const MAX_RESUME_BYTES = 6 * 1024 * 1024 // 6 MB — matches the FastAPI cap

// Validate + parse a chosen resume file, driving the provided state setters and
// antd `message` API. `deps` is injected so this is trivially testable:
//   { parseResume, message, setResumeUploading, setResumeText,
//     setResumeFileName, setResumeFileSize, maxBytes? }
// Returns Promise<boolean> — true only when a non-empty resume was loaded.
// (antd's beforeUpload ignores the return value; callers return `false`
// synchronously to stop antd's auto-upload. The boolean is for tests + reuse.)
export async function handleResumeFile(file, deps) {
  const {
    parseResume,
    message,
    setResumeUploading,
    setResumeText,
    setResumeFileName,
    setResumeFileSize,
    maxBytes = MAX_RESUME_BYTES,
  } = deps || {}

  if (!file) return false

  if (file.size > maxBytes) {
    message.error('Resume must be under 6 MB')
    return false
  }

  setResumeUploading(true)
  try {
    const { text, chars, truncated, filename } = await parseResume(file)
    if (!text || !text.trim()) {
      message.warning('No readable text in that file — try a different file.')
      return false
    }
    setResumeText(text)
    setResumeFileName(filename || file.name)
    setResumeFileSize(file.size)
    message.success(
      `Loaded ${(chars || text.length).toLocaleString()} characters${
        truncated ? ' (truncated)' : ''
      }`
    )
    return true
  } catch (err) {
    message.error(
      `Couldn't parse the file: ${
        err?.response?.data?.message || err?.message || 'unknown error'
      }`
    )
    return false
  } finally {
    setResumeUploading(false)
  }
}
